import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, UserPlus, Plus, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import SupervisorCard from "@/components/SupervisorCard";
import AddSupervisorModal from "@/components/AddSupervisorModal";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Directory() {
  const { user, setAuthOpen } = useAuth();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");
  const [company, setCompany] = useState("all");
  const [department, setDepartment] = useState("all");
  const [sort, setSort] = useState("rating");
  const [sups, setSups] = useState([]);
  const [filters, setFilters] = useState({ companies: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);

  const openAdd = () => {
    if (!user) { setAuthOpen(true); toast.info("Sign in to add a supervisor"); return; }
    setAddOpen(true);
  };

  useEffect(() => {
    api.get("/supervisors/filters").then((r) => setFilters(r.data));
  }, []);

  const PAGE = 12;
  const load = useCallback(async (nextSkip = 0) => {
    if (nextSkip === 0) setLoading(true);
    try {
      const { data } = await api.get("/supervisors", {
        params: {
          search,
          company: company === "all" ? "" : company,
          department: department === "all" ? "" : department,
          sort,
          limit: PAGE,
          skip: nextSkip,
        },
      });
      const items = data.items || [];
      setSups((prev) => (nextSkip === 0 ? items : [...prev, ...items]));
      setHasMore(!!data.has_more);
      setSkip(nextSkip + items.length);
    } catch (e) {
      if (nextSkip === 0) setSups([]);
    } finally {
      setLoading(false);
    }
  }, [search, company, department, sort]);

  useEffect(() => {
    const t = setTimeout(() => load(0), 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (search) setParams({ search }, { replace: true });
  }, [search]); // eslint-disable-line

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <h1 className="font-display text-5xl text-slate-900" style={{ fontWeight: 900 }}>Supervisor Directory</h1>
        <Button data-testid="directory-add-supervisor-btn" onClick={openAdd} className="bg-slate-900 hover:bg-emerald-600 rounded-full gap-2">
          <UserPlus size={16} /> Add Supervisor
        </Button>
      </div>
      <p className="text-slate-500 mb-2">Search and filter leaders reviewed by their teams.</p>
      <p className="text-xs text-slate-400 mb-8 flex items-center gap-1.5" data-testid="directory-anonymity-note">
        <Lock size={12} className="text-emerald-600" /> Supervisors with fewer than 5 reviews are unlisted to protect reviewer anonymity — search by name to find and review them.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="directory-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company or title..."
            className="pl-11 h-11 border-slate-300"
          />
        </div>
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger data-testid="filter-company-select" className="lg:w-48 h-11"><SelectValue placeholder="Company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All companies</SelectItem>
            {filters.companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger data-testid="filter-department-select" className="lg:w-48 h-11"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {filters.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="filter-sort-select" className="lg:w-44 h-11">
            <SlidersHorizontal size={15} className="text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rating">Highest rated</SelectItem>
            <SelectItem value="reviews">Most reviews</SelectItem>
            <SelectItem value="alpha">A – Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-slate-400 font-mono text-sm">Loading...</p>
      ) : sups.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl" data-testid="directory-empty">
          <UserPlus className="mx-auto text-slate-300 mb-3" size={40} />
          <p className="text-slate-600 font-medium">No supervisors found{search ? ` for "${search}"` : ""}.</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Can't find who you're looking for? Add them — they'll be visible to everyone.</p>
          <Button data-testid="empty-add-supervisor-btn" onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2">
            <Plus size={16} /> Add {search ? `"${search}"` : "a supervisor"}
          </Button>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="supervisor-grid">
            {sups.map((s) => <SupervisorCard key={s.id} sup={s} />)}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button data-testid="load-more-supervisors-btn" onClick={() => load(skip)} variant="outline" className="rounded-full px-8">
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <AddSupervisorModal open={addOpen} onOpenChange={setAddOpen} initialName={sups.length === 0 ? search : ""} onCreated={() => load(0)} />
    </div>
  );
}
